import { useCallback, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';

import { InfoMessage } from '@/components/common/InfoMessage.tsx';
import { Button } from '@/components/ui/button.tsx';
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field.tsx';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group.tsx';
import {
  CARRIER_TYPE,
  CARRIER_TYPE_ORDER,
  type CarrierType,
  type VerifiedInsurance,
  carrierTypesShareFamily,
} from '@/types/insurance.type.ts';

const yesNoOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
] as const;

const formId = 'insurance-plan-prerequisites-form';
const createSchema = (displayedOptions: Set<CarrierType>) => {
  return (
    z
      .object({
        medicaid: z
          .enum(
            yesNoOptions.map((option) => option.value),
            { message: 'Please make a selection' }
          )
          .optional(),
        medicare: z
          .enum(
            yesNoOptions.map((option) => option.value),
            { message: 'Please make a selection' }
          )
          .optional(),
      })
      // only require fields that are displayed on screen
      .refine(
        (data) =>
          displayedOptions.has(CARRIER_TYPE.MEDICARE) ? data.medicare !== undefined : true,
        {
          message: 'Please make a selection',
          path: ['medicare'],
        }
      )
      .refine(
        (data) =>
          displayedOptions.has(CARRIER_TYPE.MEDICAID) ? data.medicaid !== undefined : true,
        {
          message: 'Please make a selection',
          path: ['medicaid'],
        }
      )
  );
};

type FormData = z.infer<ReturnType<typeof createSchema>>;
const formCarrierTypes: CarrierType[] = [CARRIER_TYPE.MEDICARE, CARRIER_TYPE.MEDICAID];

type Props = {
  carrierTypes: CarrierType[];
  insurances: VerifiedInsurance[];
  onSubmit: (patientsCarrierTypes: Set<CarrierType>) => void;
};

// renders a form that takes in which insurance options the user will provide
export default function InsuranceInputPrepForm({ carrierTypes, insurances, onSubmit }: Props) {
  const hasMedicare = insurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, CARRIER_TYPE.MEDICARE)
  );
  const hasMedicaid = insurances.some((i) =>
    carrierTypesShareFamily(i.carrier.carrierType, CARRIER_TYPE.MEDICAID)
  );

  const processAndSubmit = useCallback(
    (formData: FormData) => {
      const carrierTypes = new Set<CarrierType>();
      if (!hasMedicare && formData.medicare === 'yes') carrierTypes.add(CARRIER_TYPE.MEDICARE);
      if (!hasMedicaid && formData.medicaid === 'yes') carrierTypes.add(CARRIER_TYPE.MEDICAID);
      onSubmit(carrierTypes);
    },
    [hasMedicare, hasMedicaid, onSubmit]
  );

  const displayedCarriers = useMemo(
    () =>
      new Set(
        carrierTypes.filter(
          (type) =>
            formCarrierTypes.some((allowedType) => type === allowedType) &&
            insurances.every(
              (insurance) => !carrierTypesShareFamily(insurance.carrier.carrierType, type)
            )
        )
      ),
    [carrierTypes, insurances]
  );

  const schema = useMemo(() => createSchema(displayedCarriers), [displayedCarriers]);

  const form = useForm<FormData>({ resolver: zodResolver(schema) });

  const willProvideMedicare = form.watch('medicare') === 'yes';
  const willProvideMedicaid = form.watch('medicaid') === 'yes';

  const willProvideInsurance =
    (!hasMedicare && willProvideMedicare) || (!hasMedicaid && willProvideMedicaid);
  const willProvideMultiple = willProvideMedicare && willProvideMedicaid;

  const orderedControllers = CARRIER_TYPE_ORDER.filter((type) => displayedCarriers.has(type)).map(
    (type) => {
      const ControllerComponent = CARRIER_CONTROLLERS[type];
      return <ControllerComponent key={type} form={form} />;
    }
  );

  return (
    <form
      id={formId}
      onSubmit={form.handleSubmit(processAndSubmit)}
      className="grid grid-cols-1 gap-8 sm:gap-12"
    >
      <FieldGroup>{orderedControllers}</FieldGroup>

      {form.formState.isValid && willProvideInsurance && (
        <InfoMessage>
          Great—grab your insurance {willProvideMultiple ? 'cards' : 'card'}, we’ll ask for that
          info next.
        </InfoMessage>
      )}

      <Button className="w-full justify-self-center sm:w-auto sm:justify-self-start" type="submit">
        Continue
      </Button>
    </form>
  );
}

const CARRIER_CONTROLLERS: Record<
  CarrierType,
  React.ComponentType<{ form: ReturnType<typeof useForm<FormData>> }>
> = {
  [CARRIER_TYPE.MEDICAID]: MedicaidController,
  [CARRIER_TYPE.MEDICAID_MANAGED_CARE]: MedicaidController,
  [CARRIER_TYPE.MEDICARE]: MedicareController,
  [CARRIER_TYPE.MEDICARE_ADVANTAGE]: MedicareController,
};

function MedicaidController({ form }: { form: ReturnType<typeof useForm<FormData>> }) {
  return (
    <Controller
      name="medicaid"
      control={form.control}
      render={({ field, fieldState }) => (
        <FieldSet>
          <FieldLegend>Do you have active Medicaid coverage?</FieldLegend>
          <RadioGroup
            className="flex flex-wrap"
            name={field.name}
            value={field.value ?? ''}
            onValueChange={field.onChange}
            aria-invalid={fieldState.invalid}
          >
            {yesNoOptions.map((option) => (
              <Field
                orientation="horizontal"
                data-invalid={fieldState.invalid}
                key={option.value}
                className="flex w-auto items-center gap-2"
              >
                <RadioGroupItem
                  value={option.value}
                  id={`${formId}-medicaid-${option.value}`}
                  aria-invalid={fieldState.invalid}
                />
                <FieldLabel htmlFor={`${formId}-medicaid-${option.value}`}>
                  {option.label}
                </FieldLabel>
              </Field>
            ))}
          </RadioGroup>
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </FieldSet>
      )}
    />
  );
}

function MedicareController({ form }: { form: ReturnType<typeof useForm<FormData>> }) {
  return (
    <Controller
      name="medicare"
      control={form.control}
      render={({ field, fieldState }) => (
        <FieldSet>
          <FieldLegend>Do you have active Medicare coverage?</FieldLegend>
          <RadioGroup
            className="flex flex-wrap"
            name={field.name}
            value={field.value ?? ''}
            onValueChange={field.onChange}
            aria-invalid={fieldState.invalid}
          >
            {yesNoOptions.map((option) => (
              <Field
                orientation="horizontal"
                data-invalid={fieldState.invalid}
                key={option.value}
                className="flex w-auto items-center gap-2"
              >
                <RadioGroupItem
                  value={option.value}
                  id={`${formId}-medicare-${option.value}`}
                  aria-invalid={fieldState.invalid}
                />
                <FieldLabel htmlFor={`${formId}-medicare-${option.value}`}>
                  {option.label}
                </FieldLabel>
              </Field>
            ))}
          </RadioGroup>
          {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
        </FieldSet>
      )}
    />
  );
}
